import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp, Clock, HardDrive, Cpu, GitBranch, X, Activity, Download, Layers, FolderOpen, Trash2, Edit2, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from './contexts/AuthContext';
import ApiService from './services/api';
import sessionApiService from './services/sessionApi';
// Dynamic hardware loading will replace this hardcoded import
import { mockModelLayers, mockRevisions, mockModelMetrics } from './data/mockData';
import UnifiedHeader from './components/UnifiedHeader';
import { exportSessionToPython } from './utils/pythonExport';
import ReadmeEditor from './components/ReadmeEditor';

// Default README content for models without README
const getDefaultReadme = (modelName) => {
  return "No readme available";
};

const OptimizeHub = () => {
  const { modelId, hardware } = useParams();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();

  const [selectedLayer, setSelectedLayer] = useState(null);
  const [activeTab, setActiveTab] = useState('explorer');
  const [modelData, setModelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInspector, setShowInspector] = useState(false);
  const [selectedExplorerRevision, setSelectedExplorerRevision] = useState(null);
  const [realRevisions, setRealRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);

  // Enhanced revision explorer state
  const [currentRevisionIndex, setCurrentRevisionIndex] = useState(0); // Will be updated when revisions load
  const [showRevisionDetails, setShowRevisionDetails] = useState(false);
  const [selectedRevisionDetails, setSelectedRevisionDetails] = useState(null);

  // User sessions state
  const [userSessions, setUserSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameSessionName, setRenameSessionName] = useState('');
  const [exportingSessionId, setExportingSessionId] = useState(null);

  // Session picker for optimization flow
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [targetLayerId, setTargetLayerId] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [creatingSession, setCreatingSession] = useState(false);

  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // README state
  const [readmeData, setReadmeData] = useState(null);
  const [showReadmeEditor, setShowReadmeEditor] = useState(false);
  const [readmeLoading, setReadmeLoading] = useState(false);

  // Hardware targets state
  const [hardwareTargets, setHardwareTargets] = useState([]);
  const [hardwareLoading, setHardwareLoading] = useState(false);
  // Hardware selection modal state
  const [showHardwareModal, setShowHardwareModal] = useState(false);
  const [selectedLayerForOptimization, setSelectedLayerForOptimization] = useState(null);

  // Load real model data if modelId is provided
  useEffect(() => {
    const loadModelData = async () => {
      // Don't fetch if auth is still loading or no session
      if (authLoading || !session?.access_token) {
        return;
      }

      if (modelId) {
        try {
          setLoading(true);
          setError('');
          
          if (hardware) {
            // Hardware-specific mode: load model with hardware variant
            const response = await ApiService.getModelForHardware(modelId, hardware, session.access_token);
            setModelData(response);
          } else {
            // Hardware-agnostic mode: load model details without hardware variant
            const response = await ApiService.getModelDetails(modelId, session.access_token);
            setModelData(response);
          }
        } catch (err) {
          console.error('Error loading model:', err);

          // Handle 404 - model not found
          if (err.message.includes('404') || err.message.includes('Failed to fetch model for hardware')) {
            setError('Model not found. This model may not exist or has been removed.');

            // Redirect to home page after 3 seconds
            setTimeout(() => {
              navigate('/');
            }, 3000);
          } else {
            setError(`Failed to load model: ${err.message}`);
          }
        } finally {
          setLoading(false);
        }
      }
    };

    loadModelData();
  }, [modelId, hardware, authLoading, session]);

  // Load hardware targets dynamically
  useEffect(() => {
    const loadHardwareTargets = async () => {
      if (authLoading || !session?.access_token) {
        return;
      }

      try {
        setHardwareLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/models/hardware-targets`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        
        if (response.ok) {
          const targets = await response.json();
          setHardwareTargets(targets);
        } else {
          console.error('Failed to load hardware targets');
        }
      } catch (err) {
        console.error('Error loading hardware targets:', err);
      } finally {
        setHardwareLoading(false);
      }
    };

    loadHardwareTargets();
  }, [authLoading, session]);

  // Load real revisions data
  useEffect(() => {
    const loadRevisions = async () => {
      // Don't fetch if auth is still loading or no session
      if (authLoading || !session?.access_token || !modelId) {
        return;
      }

      try {
        setRevisionsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/models/${modelId}/revisions`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          // console.log('Loaded realRevisions:', data.revisions?.length || 0, 'revisions');
          setRealRevisions(data.revisions || []);
        } else {
          console.error('Failed to fetch revisions');
        }
      } catch (err) {
        console.error('Error loading revisions:', err);
      } finally {
        setRevisionsLoading(false);
      }
    };

    loadRevisions();
  }, [modelId, session, authLoading]);

  // Load user sessions when sessions tab is active
  useEffect(() => {
    const loadUserSessions = async () => {
      if (authLoading || !session?.access_token || !modelId || activeTab !== 'sessions') {
        return;
      }

      try {
        setSessionsLoading(true);
        const sessions = await sessionApiService.getUserSessions(
          session.access_token,
          null, // all statuses
          modelId
        );
        setUserSessions(sessions);
      } catch (err) {
        console.error('Error loading user sessions:', err);
      } finally {
        setSessionsLoading(false);
      }
    };

    loadUserSessions();
  }, [modelId, hardware, session, authLoading, activeTab]);

  // Load README data
  useEffect(() => {
    const loadReadmeData = async () => {
      if (authLoading || !session?.access_token || !modelId) {
        return;
      }

      try {
        setReadmeLoading(true);
        const data = await ApiService.getModelReadme(modelId, session.access_token);
        console.log('README data loaded:', data); // Debug log
        setReadmeData(data);
      } catch (err) {
        console.error('Error loading README:', err);
        // Set empty README data if none exists
        setReadmeData({
          content: '',
          is_owner: false,
          updated_at: null,
          updated_by: null
        });
      } finally {
        setReadmeLoading(false);
      }
    };

    loadReadmeData();
  }, [modelId, session, authLoading]);

  // Transform real model data to flat layers array with deduplication, or use fallback
  const modelLayers = React.useMemo(() => {
    if (!modelData?.layers) return mockModelLayers;

    // Create a map to deduplicate layers by ID and name
    const layerMap = new Map();

    modelData.layers.forEach((layer, index) => {
      const layerId = layer.id || layer.name || `layer_${index}`;
      const layerName = layer.name || `${index}`;

      // Use combined key to ensure uniqueness
      const key = `${layerId}_${layerName}`;

      if (!layerMap.has(key)) {
        layerMap.set(key, {
          ...layer,
          id: layer.id ?? key,
          name: layerName,
          latency: layer.latency ?? 0,
          params: layer.params ?? '0',
          flops: layer.flops ?? '0'
        });
      }
    });

    return Array.from(layerMap.values());
  }, [modelData?.layers]);

  // Calculate dynamic performance metrics from model data
  const baseModelMetrics = modelData ? {
    totalLayers: modelLayers?.length || 0,
    totalLatency: modelLayers?.reduce((sum, layer) => sum + (layer.latency || 0), 0) || 0,
    totalParameters: modelLayers?.reduce((sum, layer) =>
      sum + (parseInt(layer.params?.replace(/[,K,M]/g, '')) || 0), 0) || 0,
    averageLatency: modelLayers?.length ?
      (modelLayers.reduce((sum, layer) => sum + (layer.latency || 0), 0) / modelLayers.length) : 0,
    modelSize: modelData.model?.file_size ? `${Math.round(modelData.model.file_size / 1024 / 1024)}MB` : 'N/A',
    status: modelData.model?.status || 'unknown'
  } : mockModelMetrics;

  // Combine base model data with real revisions from API
  const revisions = React.useMemo(() => {
    if (!modelData) return mockRevisions;

    // Base upload revision
    const baseRevision = {
      id: 'uploaded_model',
      version: 'v1.0.0',
      message: `Initial upload of ${modelData.model?.name || 'model'}`,
      author: modelData.model?.is_owner ? 'You' : (modelData.model?.owner_id?.substring(0, 8) || 'User'),
      date: modelData.model?.created_at ? new Date(modelData.model.created_at).toLocaleDateString() : 'Today',
      timestamp: modelData.model?.created_at ? new Date(modelData.model.created_at).toISOString().slice(0, 19).replace('T', ' ') : new Date().toISOString().slice(0, 19).replace('T', ' '),
      branch: 'main',
      performance: {
        latency: baseModelMetrics.totalLatency,
        speedup: 1.0,
        accuracy: 95.0, // Default for uploaded model
        memory: parseInt(baseModelMetrics.modelSize) || 50,
        throughput: 100 // Default placeholder
      },
      layersModified: [], // Initial upload - no layers are "modified" yet
      layersMerged: [],
      layersRemoved: [],
      changeType: 'upload',
      diffStats: {
        additions: baseModelMetrics.totalLayers || 0,
        deletions: 0,
        files: 1 // Single model file uploaded
      },
      optimizations: ['Model Structure Analyzed'],
      changes: [
        `Analyzed model structure`,
        `Analyzed ${baseModelMetrics.totalLayers} layers`,
        `Model size: ${baseModelMetrics.modelSize}`
      ],
      details: {
        description: `Uploaded ${modelData.model?.model_type || 'PyTorch'} model with ${baseModelMetrics.totalLayers} layers.`,
        keyChanges: [
          'Model structure analyzed and extracted',
          `${baseModelMetrics.totalLayers} layers identified`,
          'Layers prepared for optimization analysis',
          'Ready for optimization analysis'
        ],
        codeChanges: `+ Uploaded model: ${modelData.model?.name || 'model'}\n+ File size: ${baseModelMetrics.modelSize}\n+ Layers: ${baseModelMetrics.totalLayers}`
      }
    };

    // Transform real API revisions to match the expected format
    const apiRevisions = realRevisions.map((revision, index) => ({
      id: revision.id,
      version: revision.version,
      message: `Optimization ${index + 1}`,
      author: revision.author,
      date: new Date(revision.created_at).toLocaleDateString(),
      timestamp: new Date(revision.created_at).toISOString().slice(0, 19).replace('T', ' '),
      branch: 'main',
      function_code: revision.function_code,  // Include code snapshot for revision viewer
      performance: {
        latency: baseModelMetrics.totalLatency - revision.total_latency_improvement || baseModelMetrics.totalLatency,
        speedup: revision.total_speedup || 1.0,
        accuracy: revision.golden_check_passed ? 95.0 : 94.0,
        memory: parseInt(baseModelMetrics.modelSize) || 50,
        throughput: 100 * revision.total_speedup || 100
      },
      layersModified: revision.affected_layer_details?.map(layer => layer.name) || [],
      layersModifiedIds: revision.affected_layer_details?.map(layer => layer.id) || [],
      layersMerged: [],
      layersRemoved: [],
      affectedLayerDetails: revision.affected_layer_details || [],
      changeType: revision.change_type,
      diffStats: {
        additions: revision.layers_affected || 0,
        deletions: 0,
        files: revision.layers_affected || 1
      },
      optimizations: [revision.message],
      changes: [
        revision.message,
        `Speedup: ${revision.total_speedup}x`,
        `Latency improvement: ${revision.total_latency_improvement.toFixed(4)}ms`
      ],
      details: {
        description: revision.message,
        keyChanges: [
          revision.message,
          `Total speedup: ${revision.total_speedup}x`,
          `Layers affected: ${revision.layers_affected}`,
          `Golden check: ${revision.golden_check_passed ? 'Passed' : 'Failed'}`
        ],
        codeChanges: `+ Optimization: ${revision.version}\n+ Speedup: ${revision.total_speedup}x\n+ Technique: ${revision.change_type}`
      }
    }));

    const result = [baseRevision, ...apiRevisions];
    // console.log('Constructed revisions array:', result.map(r => `${r.version} (${r.id})`));
    return result;
  }, [modelData, realRevisions, baseModelMetrics, modelLayers]);

  // Compute the current selected revision - always default to latest available
  const currentSelectedRevision = React.useMemo(() => {
    if (revisions.length === 0) return null;

    // If user has manually selected a specific revision, use that
    if (selectedExplorerRevision && revisions.find(r => r.id === selectedExplorerRevision)) {
      return selectedExplorerRevision;
    }

    // Otherwise, default to latest: if we have real revisions, use the last one; otherwise use base
    const latestRevision = realRevisions.length > 0
      ? revisions[revisions.length - 1]
      : revisions[0];

    // console.log('Auto-selecting latest revision:', latestRevision.version, latestRevision.id);
    return latestRevision.id;
  }, [revisions, realRevisions, selectedExplorerRevision]);

  // Set initial revision details and index when revisions load (simplified to avoid infinite loops)
  React.useEffect(() => {
    if (revisions.length > 0 && realRevisions.length > 0 && !selectedRevisionDetails) {
      // Only auto-open details for the latest revision on initial load
      const latestRevision = revisions[revisions.length - 1];
      setSelectedRevisionDetails(latestRevision);
      setShowRevisionDetails(true);
      setCurrentRevisionIndex(revisions.length - 1);
    }
  }, [revisions, realRevisions]);

  // Revision-aware metrics that update when selectedExplorerRevision changes
  const modelMetrics = React.useMemo(() => {
    const currentRevision = revisions.find(r => r.id === currentSelectedRevision) || revisions[0];

    // Calculate total inference time for current revision
    // If revision has optimized layers, adjust their latency based on speedup
    let totalInferenceTime = 0;
    modelLayers.forEach(layer => {
      let layerLatency = layer.latency || 0;

      // Apply speedup if this layer was optimized in the current revision
      if (currentRevision?.layersModified?.includes(layer.name)) {
        layerLatency = layerLatency / (layer.speedup || 1.0);
      }

      totalInferenceTime += layerLatency;
    });

    return {
      ...baseModelMetrics,
      // Override with revision-specific performance data if available
      totalLatency: currentRevision?.performance?.latency || baseModelMetrics.totalLatency,
      averageLatency: currentRevision?.performance?.latency || baseModelMetrics.averageLatency,
      totalInferenceTime: totalInferenceTime.toFixed(2),
    };
  }, [baseModelMetrics, revisions, currentSelectedRevision, modelLayers]);

  // const leaderboardData = mockLeaderboardData; // Unused

  /*
  // Dynamic optimization history based on selected layer - UNUSED
  const getOptimizationHistory = (layer) => {
    if (!layer) {
      return [
        { iteration: 1, runtime: 0.380, milestone: 'Baseline' },
        { iteration: 134, runtime: 0.089, milestone: 'Current' }
      ];
    }

    const baseLatency = layer.latency;
    const layerType = layer.type;

    // Generate realistic optimization progression based on layer type and current performance
    const history = [
      { iteration: 1, runtime: baseLatency * 4.2, milestone: 'Baseline' }
    ];

    // Add layer-type specific optimization steps
    if (layerType.includes('Conv')) {
      history.push(
        { iteration: 15, runtime: baseLatency * 3.6, milestone: '' },
        { iteration: 28, runtime: baseLatency * 2.8, milestone: 'Winograd' },
        { iteration: 45, runtime: baseLatency * 2.0, milestone: '' },
        { iteration: 67, runtime: baseLatency * 1.6, milestone: 'TensorCore' },
        { iteration: 89, runtime: baseLatency * 1.3, milestone: '' },
        { iteration: 112, runtime: baseLatency * 1.1, milestone: 'Fused Kernel' }
      );
    } else if (layerType.includes('Linear')) {
      history.push(
        { iteration: 12, runtime: baseLatency * 3.2, milestone: '' },
        { iteration: 24, runtime: baseLatency * 2.4, milestone: 'GEMM Opt' },
        { iteration: 36, runtime: baseLatency * 1.8, milestone: '' },
        { iteration: 48, runtime: baseLatency * 1.4, milestone: 'Quantization' },
        { iteration: 64, runtime: baseLatency * 1.2, milestone: '' }
      );
    } else if (layerType.includes('BatchNorm') || layerType.includes('ReLU')) {
      history.push(
        { iteration: 8, runtime: baseLatency * 2.1, milestone: '' },
        { iteration: 16, runtime: baseLatency * 1.5, milestone: 'Kernel Fusion' },
        { iteration: 24, runtime: baseLatency * 1.2, milestone: '' }
      );
    } else {
      // Generic optimization path for other layer types
      history.push(
        { iteration: 10, runtime: baseLatency * 2.5, milestone: '' },
        { iteration: 25, runtime: baseLatency * 1.8, milestone: 'General Opt' },
        { iteration: 40, runtime: baseLatency * 1.3, milestone: '' }
      );
    }

    // Add current performance as final point
    history.push({
      iteration: history[history.length - 1].iteration + 15,
      runtime: baseLatency,
      milestone: 'Current Best'
    });

    return history;
  };
  */

  // NO MORE FAKE OPTIMIZATION HISTORY - Use LayerOptimizationChart component
  // const iterationHistory = getOptimizationHistory(selectedLayer);


  /*
  const triggerLayerAnimation = (revision) => {
    if (revision.layersMerged && revision.layersMerged.length > 0) {
      setAnimationState({
        type: 'merge',
        layers: revision.layersMerged.flat(),
        isPlaying: true
      });
      setTimeout(() => setAnimationState({ type: null, layers: [], isPlaying: false }), 2000);
    } else if (revision.layersRemoved && revision.layersRemoved.length > 0) {
      setAnimationState({
        type: 'remove',
        layers: revision.layersRemoved,
        isPlaying: true
      });
      setTimeout(() => setAnimationState({ type: null, layers: [], isPlaying: false }), 2000);
    }
  };
  */

  const handleCardClick = (revision, index) => {
    setSelectedRevisionDetails(revision);
    setShowRevisionDetails(true);
    setCurrentRevisionIndex(index);
    // Also sync the main dropdown selection
    setSelectedExplorerRevision(revision.id);

    // Trigger animation for merge/remove operations (disabled)
    // triggerLayerAnimation(revision);
  };

  // Session handlers
  const handleLoadSession = async (sessionData) => {
    // Navigate to the code editor with the first layer of this session
    // Pass the session ID in the URL so it auto-loads
    const firstLayer = modelData?.layers?.[0];
    if (firstLayer) {
      const optimizePath = hardware
        ? `/models/${modelId}/${hardware}/optimize/${firstLayer.id}`
        : `/models/${modelId}/optimize/${firstLayer.id}`;
      navigate(`${optimizePath}?sessionId=${sessionData.id}`);
    }
  };

  const handleDeleteSession = (sessionId, sessionName) => {
    setSessionToDelete({ id: sessionId, name: sessionName });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;

    console.log('Deleting session:', sessionToDelete.id);
    console.log('Current userSessions before delete:', userSessions);

    try {
      setDeletingSessionId(sessionToDelete.id);
      setShowDeleteConfirm(false);
      // Use hard delete (true) to permanently remove from database
      const deleteResult = await sessionApiService.deleteSession(sessionToDelete.id, true, session.access_token);
      console.log('Delete result:', deleteResult);

      // Refresh sessions list
      const sessions = await sessionApiService.getUserSessions(
        session.access_token,
        null,
        modelId
      );
      console.log('Refreshed sessions after delete:', sessions);
      setUserSessions(sessions);
      console.log('Updated userSessions state');
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Failed to delete session. Please try again.');
    } finally {
      setDeletingSessionId(null);
      setSessionToDelete(null);
    }
  };

  const cancelDeleteSession = () => {
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  };

  const handleStartRename = (sessionItem) => {
    setRenamingSessionId(sessionItem.id);
    setRenameSessionName(sessionItem.session_name || '');
  };

  const handleCancelRename = () => {
    setRenamingSessionId(null);
    setRenameSessionName('');
  };

  const handleSaveRename = async (sessionId) => {
    if (!renameSessionName.trim()) {
      alert('Session name cannot be empty');
      return;
    }

    try {
      await sessionApiService.updateSession(
        sessionId,
        { session_name: renameSessionName.trim() },
        session.access_token
      );

      // Refresh sessions list
      const sessions = await sessionApiService.getUserSessions(
        session.access_token,
        null,
        modelId
      );
      setUserSessions(sessions);
      setRenamingSessionId(null);
      setRenameSessionName('');
    } catch (err) {
      console.error('Error renaming session:', err);
      alert('Failed to rename session. Please try again.');
    }
  };

  const handleExportSession = async (sessionItem) => {
    if (!session?.access_token) {
      alert('You must be logged in to export sessions');
      return;
    }

    setExportingSessionId(sessionItem.id);

    try {
      const exportData = await sessionApiService.exportSessionAsPython(
        sessionItem.id,
        modelId,
        session.access_token
      );

      await exportSessionToPython(exportData.session, exportData.model, exportData.layers);
    } catch (err) {
      console.error('Failed to export session:', err);
      alert(`Failed to export session: ${err.message}`);
    } finally {
      setExportingSessionId(null);
    }
  };

  const renderRevisionCard = (revision, index, isActive = false, isLatest = false) => (
    <div
      key={revision.id}
      className={`relative rounded-xl shadow-lg border-2 transition-all duration-300 cursor-pointer ${
        isActive
          ? 'border-blue-500 bg-blue-50'
          : 'border-[#1f2937] hover:border-[#374151] hover:shadow-md bg-[#1f2937]'
      }`}
      onClick={() => handleCardClick(revision, index)}
    >
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {isLatest && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                📌 Latest
              </span>
            )}
            <span className="text-lg font-bold text-orange-500">{revision.version}</span>
            <span className="text-xs text-green-600 font-semibold">{revision.performance.latency}ms</span>
          </div>
          <span className="text-xs text-gray-500">{revision.timestamp ? revision.timestamp.split(' ')[0] : (revision.date || 'Today')}</span>
        </div>

        <h3 className="text-sm font-semibold text-white mb-2 truncate">{revision.message}</h3>

        <div className="text-xs text-gray-400">
          <span>{revision.author}</span>
        </div>
      </div>
    </div>
  );

  // Show loading state while model data is being fetched
  if (loading && modelId) {
    return (
      <div className="min-h-screen relative">
        <div className="fixed inset-0 bg-[#0b0f19]" />
        <div className="fixed inset-0" style={{ background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(120, 119, 198, 0.15), transparent)' }} />
        <div className="relative flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-white mb-2">Loading model data...</h3>
            <p className="text-gray-400">Please wait while we analyze your model</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle hardware selection for optimization
  const handleHardwareSelection = async (selectedHardware) => {
    setShowHardwareModal(false);
    
    if (selectedLayerForOptimization) {
      await handleOptimizeLayer(selectedLayerForOptimization, selectedHardware.slug);
    }
    
    setSelectedLayerForOptimization(null);
  };

  // Handle optimization from Hardware Targets tab
  const handleOptimizeForHardware = async (hardwareSlug) => {
    // Find first editable layer
    const firstEditableLayer = modelLayers.find(layer => !layer.is_read_only);
    if (firstEditableLayer) {
      await handleOptimizeLayer(firstEditableLayer, hardwareSlug);
    } else {
      alert('No editable layers found for optimization');
    }
  };

  // Optimization handler - check for sessions first, then navigate
  const handleOptimizeLayer = async (layer, hardwareTarget = null) => {
    setTargetLayerId(layer.id);
    
    // If hardware is provided, skip session check and go directly to session creation
    if (hardwareTarget) {
      // Store the target hardware for session navigation/creation
      window.sessionTargetHardware = hardwareTarget;
      setShowNewSessionDialog(true);
      return;
    }

    try {
      // Check if there are active sessions for this model
      const result = await sessionApiService.findActiveSession(modelId, session.access_token);

      if (result.has_sessions && result.sessions.length > 0) {
        // Show session picker modal
        setAvailableSessions(result.sessions);
        setShowSessionPicker(true);
      } else {
        // No sessions, get first editable layer and show hardware selection
        const firstEditableLayer = modelLayers.find(l => !l.is_read_only);
        if (firstEditableLayer) {
          setSelectedLayerForOptimization(firstEditableLayer);
          setShowHardwareModal(true);
        }
      }
    } catch (err) {
      console.error('Error checking for sessions:', err);
      // On error, show hardware selection
      const firstEditableLayer = modelLayers.find(l => !l.is_read_only);
      if (firstEditableLayer) {
        setSelectedLayerForOptimization(firstEditableLayer);
        setShowHardwareModal(true);
      }
    }
  };

  // Handle selecting an existing session
  const handleSelectExistingSession = (sessionData) => {
    setShowSessionPicker(false);
    // Use the session's hardware target if available, otherwise fall back to current context
    const targetHardware = sessionData.hardware_target || hardware;
    const optimizePath = targetHardware
      ? `/models/${modelId}/${targetHardware}/optimize/${targetLayerId}`
      : `/models/${modelId}/optimize/${targetLayerId}`;
    navigate(`${optimizePath}?sessionId=${sessionData.id}`);
  };

  // Handle creating a new session
  const handleCreateNewSession = async () => {
    if (!newSessionName.trim()) {
      alert('Please enter a session name');
      return;
    }

    const targetHardware = window.sessionTargetHardware || hardware;

    try {
      setCreatingSession(true);
      const newSession = await sessionApiService.createSession(
        modelId,
        targetHardware,
        newSessionName.trim(),
        session.access_token
      );

      setShowNewSessionDialog(false);
      setNewSessionName('');
      const optimizePath = targetHardware
        ? `/models/${modelId}/${targetHardware}/optimize/${targetLayerId}`
        : `/models/${modelId}/optimize/${targetLayerId}`;
      navigate(`${optimizePath}?sessionId=${newSession.id}`);
    } catch (err) {
      console.error('Failed to create session:', err);
      alert(`Failed to create session: ${err.message}`);
    } finally {
      setCreatingSession(false);
    }
  };

  // Handle starting new session from picker
  const handleStartNewFromPicker = () => {
    setShowSessionPicker(false);
    // Get the first editable layer (same as main optimize button)
    const firstEditableLayer = modelLayers.find(layer => !layer.is_read_only);
    if (firstEditableLayer) {
      setSelectedLayerForOptimization(firstEditableLayer);
      setShowHardwareModal(true);
    }
  };

  // Show error state if model data failed to load
  if (error && modelId) {
    const isModelNotFound = error.includes('not found') || error.includes('404');

    return (
      <div className="min-h-screen relative">
        <div className="fixed inset-0 bg-[#0b0f19]" />
        <div className="fixed inset-0" style={{ background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(120, 119, 198, 0.15), transparent)' }} />
        <div className="relative flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-red-400 mb-4">
            {isModelNotFound ? (
              <div className="text-6xl mb-4">🔍</div>
            ) : (
              <X className="h-12 w-12 mx-auto" />
            )}
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {isModelNotFound ? 'Model Not Found' : 'Failed to load model'}
          </h3>
          <p className="text-gray-400 mb-4">{error}</p>
          {isModelNotFound ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Redirecting to home page in 3 seconds...</p>
              <button
                onClick={() => navigate('/')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
              >
                Go to Home Page
              </button>
            </div>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
            >
              Try Again
            </button>
          )}
        </div>
        </div>
      </div>
    );
  }

  // README handlers
  const handleEditReadme = () => {
    setShowReadmeEditor(true);
  };

  const handleSaveReadme = async (content) => {
    try {
      await ApiService.updateModelReadme(modelId, content, session.access_token);
      // Refresh README data
      const updatedData = await ApiService.getModelReadme(modelId, session.access_token);
      setReadmeData(updatedData);
      setShowReadmeEditor(false);
    } catch (error) {
      console.error('Error saving README:', error);
      alert(`Failed to save README: ${error.message}`);
    }
  };

  const handleCancelReadmeEdit = () => {
    setShowReadmeEditor(false);
  };

  return (
    <div className="min-h-screen relative">
      {/* Gradient Background */}
      <div className="fixed inset-0 bg-[#0b0f19]" />
      <div
        className="fixed inset-0"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(120, 119, 198, 0.15), transparent)'
        }}
      />

      <div className="relative">
        <UnifiedHeader
          showSearch={false}
          showUpload={true}
          showAK47={true}
        />

        {/* Navigation */}
        <div className="mx-[15%] relative pb-0">
          <div className="flex space-x-2 relative z-10">
          {[
            { id: 'explorer', name: 'Model Explorer', icon: GitBranch },
            { id: 'hardware-targets', name: 'Hardware Targets', icon: HardDrive },
            { id: 'revisions', name: 'Revision Explorer', icon: GitBranch },
            { id: 'sessions', name: 'Your Sessions', icon: FolderOpen }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 transition-all relative ${
                activeTab === tab.id 
                  ? 'bg-[#151b27] text-orange-600 rounded-t-lg border-b-2 border-orange-600' 
                  : 'bg-transparent text-gray-400 hover:text-white hover:bg-transparent mb-1'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </div>
        </div>

      {activeTab === 'revisions' && (
        <div className="h-screen">
          <div className="mx-[15%] space-y-6 p-6">
            {/* Revision header with view mode selector */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">Optimization History</h1>
                <p className="text-gray-400 mt-1">
                  Exploring {revisions.length} optimization iterations over time
                </p>
              </div>

            </div>

            <div className="flex gap-6" style={{ height: 'calc(100vh - 200px)' }}>
                {/* Left side - Cards list (25%) */}
                <div className="w-1/4 flex flex-col space-y-4">
                  {/* Card navigation header */}
                  <div className="bg-[#1f2937] p-4 rounded-lg border border-[#1f2937] shadow-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-300">Optimization History</span>
                      <span className="text-sm text-gray-400">
                        {revisions.length} revisions
                      </span>
                    </div>
                  </div>

                  {/* Cards list - newest to oldest */}
                  <div className="flex-1 overflow-y-auto space-y-3 px-2 py-2">
                    {[...revisions].reverse().map((revision, reverseIndex) => {
                      const originalIndex = revisions.length - 1 - reverseIndex;
                      const isActive = originalIndex === currentRevisionIndex;
                      const isLatest = reverseIndex === 0;
                      return (
                        <div
                          key={revision.id}
                          className="transition-all duration-200"
                          style={{ margin: '8px 0' }}
                        >
                          {renderRevisionCard(revision, originalIndex, isActive, isLatest)}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Combined Model Explorer & Details Panel */}
                <div className="w-3/4">
                  {showRevisionDetails && selectedRevisionDetails ? (
                    <div className="h-full overflow-hidden flex flex-col">
                      {/* Header - Sticky */}
                      <div className="p-4 border-b border-[#1f2937] bg-[#0b0f19] flex-shrink-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <Layers className="h-5 w-5 text-orange-500" />
                            <h3 className="text-lg font-semibold text-white">Model Explorer</h3>
                            <span className="text-sm text-gray-400">({selectedRevisionDetails.version})</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#151b27] text-gray-800 border border-[#1f2937]">
                              <Clock className="h-3 w-3 mr-1" />
                              {selectedRevisionDetails.performance.latency}ms
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              {selectedRevisionDetails.performance.speedup}×
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              <Activity className="h-3 w-3 mr-1" />
                              {selectedRevisionDetails.performance.throughput.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => setShowRevisionDetails(false)}
                              className="text-gray-400 hover:text-gray-400 p-2"
                            >
                              <X className="w-6 h-6" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-400">{selectedRevisionDetails.message}</p>
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
                          {/* Left Column - Model Layers */}
                          <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-white">Function Diff's</h4>
                            {/* Render all model layers with color coding for this revision */}
                            <div className="space-y-2">
                              {modelLayers.map(layer => {
                                // Get current revision for layer state
                                const currentRevision = selectedRevisionDetails;

                                // Determine layer status for this revision
                                const isInitialUpload = currentRevision?.changeType === 'upload';
                                const isModified = !isInitialUpload && (currentRevision?.layersModified?.includes(layer.name) ||
                                                  currentRevision?.layersModified?.includes(layer.id));
                                const isMerged = !isInitialUpload && currentRevision?.layersMerged?.some(group =>
                                  group.includes(layer.name) || group.includes(layer.id));
                                const isRemoved = !isInitialUpload && (currentRevision?.layersRemoved?.includes(layer.name) ||
                                                 currentRevision?.layersRemoved?.includes(layer.id));

                                const layerIsAffected = isModified || isMerged || isRemoved;

                                // Set styling based on revision state
                                let layerStyle = 'bg-[#151b27] border border-[#1f2937]'; // Neutral/unaffected
                                
                                if (isRemoved) {
                                  layerStyle = 'bg-red-100 border border-red-200'; // Red for removed
                                } else if (isMerged) {
                                  layerStyle = 'bg-green-100 border border-green-200'; // Green for merged
                                } else if (isModified) {
                                  layerStyle = 'bg-yellow-100 border border-yellow-200'; // Yellow for modified
                                }

                                return (
                                  <div
                                    key={layer.id}
                                    className={`px-3 py-2 rounded-full cursor-pointer hover:bg-opacity-80 transition-all flex items-center justify-between ${layerStyle}`}
                                    onClick={() => {
                                      setSelectedLayer(layer);
                                      setShowInspector(true);
                                    }}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <h3 className="font-medium text-sm truncate">{layer.name}</h3>
                                    </div>
                                    {layerIsAffected && (
                                      <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                          </div>

                          {/* Right Column - Details */}
                          <div className="space-y-5">
                            {/* Author and Date near pills */}
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center space-x-4">
                                <div>
                                  <span className="font-medium text-gray-400">Author:</span>
                                  <span className="text-white ml-1">{selectedRevisionDetails.author}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-400">Date:</span>
                                  <span className="text-white ml-1">{selectedRevisionDetails.timestamp ? selectedRevisionDetails.timestamp.split(' ')[0] : (selectedRevisionDetails.date || 'Today')}</span>
                                </div>
                              </div>
                            </div>

                            {/* Code Diff Section */}
                            <div>
                              <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                                <span className="mr-2">💻</span>
                                Code Diff
                              </h4>

                              {/* Collapsed summary */}
                              <div className="bg-[#0b0f19] p-3 rounded-lg border border-[#1f2937]">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-4">
                                    <span className="text-sm font-medium text-gray-300">
                                      {selectedRevisionDetails.diffStats?.files || 1} files changed
                                    </span>
                                    <span className="text-sm text-green-600 font-medium">
                                      +{selectedRevisionDetails.diffStats?.additions || 0}
                                    </span>
                                    <span className="text-sm text-red-600 font-medium">
                                      -{selectedRevisionDetails.diffStats?.deletions || 0}
                                    </span>
                                  </div>
                                  <a href="#" className="text-orange-500 hover:text-blue-800 text-sm font-medium">
                                    View Diff
                                  </a>
                                </div>
                              </div>
                            </div>


                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#0b0f19] rounded-lg border-2 border-dashed border-[#374151] h-full flex items-center justify-center">
                      <div className="text-center">
                        <GitBranch className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-400 mb-2">Select a Revision</h3>
                        <p className="text-gray-500">Click on any revision card to view detailed information</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'explorer' && (
        <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 mb-6">
          {/* Page Title */}
        </div>

        <div className="mx-[15%] px-6">
          <div className="space-y-6">
            {/* Model Title with pills */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-white">
                  {modelData?.model?.name || "Suhail's Resnet"}
                </h1>
                <button
                  onClick={() => {
                    // Find first editable (non-read-only) layer
                    const firstEditableLayer = modelLayers.find(layer => !layer.is_read_only);
                    if (firstEditableLayer) {
                      handleOptimizeLayer(firstEditableLayer);
                    }
                  }}
                  disabled={!modelLayers.find(layer => !layer.is_read_only)}
                  className="px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Optimize
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
                  <Clock className="h-4 w-4 mr-1" />
                  Inference Time: {modelMetrics.totalInferenceTime}ms
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#151b27] text-gray-800 border border-[#1f2937]">
                  <Layers className="h-4 w-4 mr-1" />
                  Total Layers: {modelMetrics.totalLayers}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                  <HardDrive className="h-4 w-4 mr-1" />
                  Model Size: {modelMetrics.modelSize}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                  <GitBranch className="h-4 w-4 mr-1" />
                  # of Revisions: {revisions.length}
                </span>
              </div>
            </div>

            {/* Two-column layout: Golden Functions (left 33%) and README (right 67%) */}
            <div className="flex gap-6">
              {/* Left Column - Golden Functions (33%) */}
              <div className="w-1/3 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-white">Golden Functions</h2>
                  <p className="text-sm text-gray-400 mt-1">Editable layers with change indicators for current revision</p>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {modelLayers
                    .filter(layer => !layer.is_read_only)  // Only show editable functions
                    .map(layer => {
                    // Get current revision for layer state
                    const currentRevision = revisions.find(r => r.id === currentSelectedRevision);

                    // Determine layer status for this revision
                    const isInitialUpload = currentRevision?.changeType === 'upload';
                    const isModified = !isInitialUpload && (currentRevision?.layersModified?.includes(layer.name) ||
                                      currentRevision?.layersModified?.includes(layer.id));
                    const isMerged = !isInitialUpload && currentRevision?.layersMerged?.some(group =>
                      group.includes(layer.name) || group.includes(layer.id));
                    const isRemoved = !isInitialUpload && (currentRevision?.layersRemoved?.includes(layer.name) ||
                                     currentRevision?.layersRemoved?.includes(layer.id));

                    return (
                      <div
                        key={layer.id}
                        className="px-3 py-2 bg-[#151b27] border border-[#1f2937] rounded-full transition-all"
                      >
                        <h3 className="font-medium text-sm text-white truncate">{layer.name}</h3>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="w-px bg-gray-300"></div>

              {/* Right Column - Dynamic README (67%) */}
              <div className="flex-1">
                {showReadmeEditor ? (
                  <div className="h-[600px] bg-[#1f2937] rounded-lg border border-[#1f2937]">
                    <ReadmeEditor
                      modelId={modelId}
                      initialContent={readmeData?.content || ''}
                      isOwner={readmeData?.is_owner || false}
                      onSave={handleSaveReadme}
                      onCancel={handleCancelReadmeEdit}
                      apiService={{
                        getAccessToken: () => session?.access_token
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-white">README</h2>
                      {readmeData?.is_owner && (
                        <button
                          onClick={handleEditReadme}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-orange-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span>Edit README</span>
                        </button>
                      )}
                      {/* Debug info - remove in production */}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="text-xs text-gray-400 mt-1">
                          Debug: is_owner={String(readmeData?.is_owner)}, readmeData={JSON.stringify(readmeData)}
                        </div>
                      )}
                    </div>
                    
                    {readmeLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none text-gray-300">
                        <ReactMarkdown
                          className="markdown-content"
                          components={{
                            h1: ({children}) => <h1 className="text-xl font-bold text-white mb-3 mt-6 first:mt-0">{children}</h1>,
                            h2: ({children}) => <h2 className="text-lg font-semibold text-white mb-2 mt-5 first:mt-0">{children}</h2>,
                            h3: ({children}) => <h3 className="text-base font-semibold text-white mb-2 mt-4 first:mt-0">{children}</h3>,
                            p: ({children}) => <p className="mb-3 text-sm leading-relaxed">{children}</p>,
                            ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-3 text-sm">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-3 text-sm">{children}</ol>,
                            li: ({children}) => <li className="text-sm">{children}</li>,
                            code: ({children}) => <code className="bg-[#151b27] px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                            pre: ({children}) => <pre className="bg-[#151b27] p-3 rounded text-xs overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({children}) => <blockquote className="border-l-4 border-[#374151] pl-4 italic mb-3">{children}</blockquote>,
                          }}
                        >
                          {readmeData?.content || getDefaultReadme(modelData?.model?.name)}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Hardware Targets Tab */}
      {activeTab === 'hardware-targets' && (
        <div className="py-6">
          <div className="mx-[15%] px-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">Hardware Targets</h1>
                  <p className="text-gray-400 mt-1">
                    Select a hardware target to optimize {modelData?.model?.name || "this model"} for that specific platform
                  </p>
                </div>
              </div>

              {/* Hardware Targets List */}
              <div className="space-y-4">
                {hardwareLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading hardware targets...</p>
                  </div>
                ) : hardwareTargets.length > 0 ? (
                  hardwareTargets.map((hardware) => {
                    return (
                      <div
                        key={hardware.slug}
                        className="bg-[#1f2937] rounded-lg border border-[#1f2937] p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-lg bg-blue-100">
                              <HardDrive className="h-6 w-6 text-orange-500" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">{hardware.name}</h3>
                              <p className="text-sm text-gray-500">{hardware.description}</p>
                              <div className="text-xs text-gray-400 mt-1">
                                {hardware.manufacturer} • {hardware.architecture}
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleOptimizeForHardware(hardware.slug)}
                            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                          >
                            Optimize
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <HardDrive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No hardware targets available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="py-6">
          <div className="mx-[15%] px-6">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-white">Your Optimization Sessions</h1>
              <p className="text-sm text-gray-400 mt-1">
                Manage and resume your optimization work for {modelData?.model?.name || "this model"}
              </p>
            </div>

            {/* Sessions List */}
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              </div>
            ) : userSessions.length === 0 ? (
              <div className="text-center py-12 border-t border-[#1f2937]">
                <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-400 mb-1">No Sessions Yet</h3>
                <p className="text-sm text-gray-500 mb-4">Start optimizing layers to create your first session</p>
                <button
                  onClick={() => {
                    const firstLayer = modelData?.layers?.[0];
                    if (firstLayer) {
                      handleOptimizeLayer(firstLayer);
                    }
                  }}
                  disabled={!modelData?.layers?.length}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Start Optimizing
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 border-t border-[#1f2937]">
                {userSessions.map((sessionItem) => (
                  <div
                    key={sessionItem.id}
                    className="py-4 hover:bg-[#0b0f19] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      {/* Left: Session Info */}
                      <div className="flex-1 min-w-0 pr-4">
                        {/* Session Name */}
                        <div className="mb-1">
                          {renamingSessionId === sessionItem.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={renameSessionName}
                                onChange={(e) => setRenameSessionName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveRename(sessionItem.id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelRename();
                                  }
                                }}
                                className="px-2 py-1 text-base font-semibold text-white border border-[#374151] rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                autoFocus
                              />
                              <button
                                onClick={() => handleSaveRename(sessionItem.id)}
                                className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                title="Save"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelRename}
                                className="p-1 text-gray-500 hover:text-gray-300 hover:bg-[#151b27] rounded transition-colors"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <h3 className="text-base font-semibold text-white hover:underline cursor-pointer" onClick={() => handleLoadSession(sessionItem)}>
                                {sessionItem.session_name || `Session ${sessionItem.id.slice(0, 8)}`}
                              </h3>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStartRename(sessionItem); }}
                                className="p-1 text-gray-400 hover:text-gray-400 hover:bg-[#151b27] rounded transition-colors"
                                title="Rename session"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Stats Row */}
                        <div className="flex items-center gap-4 text-sm text-gray-400 mb-1">
                          <div className="flex items-center space-x-1">
                            <Cpu className="h-3.5 w-3.5 text-gray-500" />
                            <span>{sessionItem.hardware_target || hardware || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Layers className="h-3.5 w-3.5 text-gray-500" />
                            <span>{sessionItem.optimized_layers_count || 0}/{sessionItem.total_layers_count || 0} layers</span>
                          </div>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />
                          <span>
                            Updated {sessionItem.last_autosave_at
                              ? new Date(sessionItem.last_autosave_at).toLocaleString()
                              : new Date(sessionItem.updated_at).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Right: Status + Actions */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Status Badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          sessionItem.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-[#151b27] text-gray-300'
                        }`}>
                          {sessionItem.status}
                        </span>

                        {/* Action Buttons */}
                        <button
                          onClick={() => handleLoadSession(sessionItem)}
                          className="px-3 py-1.5 bg-orange-600 text-white rounded text-sm font-medium hover:bg-orange-700 transition-colors"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => handleExportSession(sessionItem)}
                          disabled={exportingSessionId === sessionItem.id}
                          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors disabled:opacity-50"
                          title="Export as Python"
                        >
                          {exportingSessionId === sessionItem.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteSession(sessionItem.id, sessionItem.session_name)}
                          disabled={deletingSessionId === sessionItem.id}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Delete session"
                        >
                          {deletingSessionId === sessionItem.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session Picker Modal */}
      {showSessionPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f2937] rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-[#1f2937]">
              <h2 className="text-2xl font-bold text-white">Choose a Session</h2>
              <p className="text-sm text-gray-400 mt-1">
                Select an existing session or start a new one
              </p>
            </div>

            <div className="p-6 space-y-3">
              {availableSessions.map((sess) => (
                <div
                  key={sess.id}
                  className="border border-[#1f2937] rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectExistingSession(sess)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-white">
                        {sess.session_name || `Session ${sess.id.slice(0, 8)}`}
                      </h3>
                      <p className="text-sm text-gray-400">
                        Last updated: {new Date(sess.updated_at).toLocaleString()}
                      </p>
                      {sess.hardware_target && (
                        <p className="text-xs text-orange-500 mt-1">
                          Hardware: {sess.hardware_target}
                        </p>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      sess.status === 'active' ? 'bg-green-100 text-green-800' :
                      sess.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-[#151b27] text-gray-800'
                    }`}>
                      {sess.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-[#1f2937] flex justify-between">
              <button
                onClick={() => setShowSessionPicker(false)}
                className="px-4 py-2 text-gray-300 hover:bg-[#151b27] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartNewFromPicker}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Start New Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Session Dialog */}
      {showNewSessionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f2937] rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-[#1f2937]">
              <h2 className="text-2xl font-bold text-white">Start New Session</h2>
              <p className="text-sm text-gray-400 mt-1">
                Give your optimization session a name
              </p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Session Name
              </label>
              <input
                type="text"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateNewSession()}
                placeholder="e.g., Conv2D Optimization"
                className="w-full px-3 py-2 border border-[#374151] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>

            <div className="p-6 border-t border-[#1f2937] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewSessionDialog(false);
                  setNewSessionName('');
                }}
                disabled={creatingSession}
                className="px-4 py-2 text-gray-300 hover:bg-[#151b27] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewSession}
                disabled={!newSessionName.trim() || creatingSession}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingSession ? 'Creating...' : 'Create Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hardware Selection Modal */}
      {showHardwareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f2937] rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-[#1f2937]">
              <h2 className="text-2xl font-bold text-white">Select Hardware Target</h2>
              <p className="text-sm text-gray-400 mt-1">
                Choose the hardware platform you want to optimize for
              </p>
            </div>
            <div className="p-6">
              {hardwareLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-400">Loading hardware targets...</span>
                </div>
              ) : hardwareTargets.length > 0 ? (
                <div className="space-y-3">
                  {hardwareTargets.map((hardware) => (
                    <div
                      key={hardware.id}
                      onClick={() => handleHardwareSelection(hardware)}
                      className="border border-[#1f2937] rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-white">{hardware.name}</h3>
                          <p className="text-sm text-gray-400">{hardware.description}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>Manufacturer: {hardware.manufacturer}</span>
                            <span>Architecture: {hardware.architecture}</span>
                          </div>
                        </div>
                        <div className="text-orange-500">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">No hardware targets available</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#1f2937] flex justify-end">
              <button
                onClick={() => setShowHardwareModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && sessionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f2937] rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-[#1f2937]">
              <h2 className="text-2xl font-bold text-white">Delete Session</h2>
              <p className="text-sm text-gray-400 mt-1">
                This action cannot be undone
              </p>
            </div>

            <div className="p-6">
              <p className="text-gray-300">
                Are you sure you want to delete the session{' '}
                <span className="font-semibold">
                  {sessionToDelete.name || `Session ${sessionToDelete.id.slice(0, 8)}`}
                </span>
                ? All optimization work in this session will be permanently removed.
              </p>
            </div>

            <div className="p-6 border-t border-[#1f2937] flex justify-end space-x-3">
              <button
                onClick={cancelDeleteSession}
                className="px-4 py-2 text-gray-300 hover:bg-[#151b27] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSession}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Session
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default OptimizeHub;
